// Authenticator enrollment — public page reached via a single-use link
// (/enroll/<token>) handed out by the admin. Shows the QR code for
// Microsoft Authenticator (or any TOTP app); confirming the first 6-digit
// code activates code-based login and consumes the token.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { apiFetch } from "../api/client";
import { Logo } from "../components/Logo";

import "../components/Login.css";
import "./Enroll.css";

interface Pending {
  username: string;
  otpauth_uri: string;
  qr_svg: string;
  secret: string;
}

type State =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "pending"; data: Pending }
  | { kind: "done"; username: string };

export function Enroll() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/enroll/${token}`);
        if (cancelled) return;
        if (!res.ok) {
          setState({ kind: "invalid" });
          return;
        }
        setState({ kind: "pending", data: (await res.json()) as Pending });
      } catch {
        if (!cancelled) setState({ kind: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || state.kind !== "pending") return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/enroll/${token}`, {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      if (res.ok) {
        setState({ kind: "done", username: state.data.username });
        return;
      }
      setError(
        res.status === 429
          ? "too many attempts — try again later"
          : "wrong code — wait for the next one and try again",
      );
      setCode("");
    } catch {
      setError("network error — is the server running?");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell enroll-shell">
      <div className="login-logo-row">
        <span className="login-logo">
          <Logo size={44} />
        </span>
        <span className="login-wordmark">cinematic database</span>
      </div>

      {state.kind === "loading" && <p className="enroll-note">loading…</p>}

      {state.kind === "invalid" && (
        <div className="enroll-card">
          <p className="enroll-note">
            This enrollment link is invalid or has expired. Ask the admin for a
            fresh one.
          </p>
        </div>
      )}

      {state.kind === "pending" && (
        <div className="enroll-card">
          <p className="enroll-note">
            Set up code login for <strong>{state.data.username}</strong>: scan
            this QR code with <strong>Microsoft Authenticator</strong> (add
            account → other), then enter the 6-digit code it shows.
          </p>
          <div
            className="enroll-qr"
            // The SVG is generated server-side by the qrcode library from the
            // otpauth URI — no external content.
            dangerouslySetInnerHTML={{ __html: state.data.qr_svg }}
          />
          <details className="enroll-manual">
            <summary>can't scan? enter the key manually</summary>
            <code>{state.data.secret}</code>
          </details>
          <form className="login-form enroll-form" onSubmit={onConfirm}>
            <div className="login-field">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="6-digit code"
                disabled={submitting}
                autoFocus
              />
              <button
                type="submit"
                className="login-submit"
                disabled={submitting || code.length !== 6}
                aria-label="Confirm code"
              >
                →
              </button>
            </div>
            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}
          </form>
        </div>
      )}

      {state.kind === "done" && (
        <div className="enroll-card">
          <p className="enroll-note">
            ✓ Authenticator active for <strong>{state.username}</strong>.
            From now on, log in with the current 6-digit code instead of your
            password.
          </p>
          <Link className="enroll-login-link" to="/login">
            go to login →
          </Link>
        </div>
      )}
    </main>
  );
}
